import { useEffect, useRef, useState } from "react";
import { useNavigation } from "@remix-run/react";

/**
 * A thin top progress bar that shows during client-side navigation, so route
 * changes feel responsive instead of frozen while loaders resolve. Mirrors the
 * pattern used by polished SaaS apps (linear/vercel-style top loader).
 */
export default function NavigationProgress() {
  const navigation = useNavigation();
  const active = navigation.state !== "idle";

  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // --- Navigation performance logging ---------------------------------------
  // Logs how long each client-side navigation takes (click -> page ready), so
  // slow route transitions are measurable from the browser console. Grep "[nav]".
  const navStart = useRef<{ t: number; from: string; to: string } | null>(null);
  useEffect(() => {
    if (navigation.state !== "idle") {
      // A navigation just started (or changed phase). Record the first phase.
      if (!navStart.current) {
        const from = typeof window !== "undefined" ? window.location.pathname : "";
        const to = navigation.location?.pathname ?? "(unknown)";
        navStart.current = { t: performance.now(), from, to };
        console.log(
          `[nav] start ${from} -> ${to} (state=${navigation.state})`,
          `@${Math.round(navStart.current.t)}ms`
        );
      } else {
        // loading -> submitting -> loading transitions: note the phase change.
        console.log(
          `[nav] phase=${navigation.state} -> ${navigation.location?.pathname ?? ""}`,
          `+${Math.round(performance.now() - navStart.current.t)}ms`
        );
      }
    } else if (navStart.current) {
      const { t, from, to } = navStart.current;
      const dur = Math.round(performance.now() - t);
      const slow = dur > 600 ? " ⚠️SLOW" : "";
      console.log(`[nav] done  ${from} -> ${to} in ${dur}ms${slow}`);
      navStart.current = null;
    }
  }, [navigation.state, navigation.location?.pathname]);

  useEffect(() => {
    const clearTimers = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    if (active) {
      clearTimers();
      setVisible(true);
      setProgress(8);
      // Ease towards 90% while we wait; never reach 100 until navigation ends.
      timers.current.push(setTimeout(() => setProgress(45), 80));
      timers.current.push(setTimeout(() => setProgress(70), 300));
      timers.current.push(setTimeout(() => setProgress(88), 800));
    } else if (visible) {
      clearTimers();
      setProgress(100);
      timers.current.push(
        setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 220)
      );
    }

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] pointer-events-none">
      <div
        className="h-full rounded-r-full shadow-[0_0_10px_rgba(109,92,248,0.6)]"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg,#6d5cf8,#5b5cf6,#ec4899)",
          transition: "width 220ms ease, opacity 220ms ease",
          opacity: progress >= 100 ? 0 : 1,
        }}
      />
    </div>
  );
}
